import request from '@/util/request'
import {default as productMixin} from '@/mixins/product'
import {default as orderMixin} from '@/mixins/order'
import {default as guardsMixin} from '@/mixins/guards'
import {createHelpers} from 'vuex-map-fields'
import {optionsToPaginationParams} from '@/util/pagination'
import _ from 'lodash'
import {FulfillmentTypes, MarkupTypes, ACH_PAYMENT_IDS} from '@/util/constants'
import { EVENT_PROVIDERS, ANALYTICS_EVENTS } from '@/util/constants'
import { sendEvent } from '@/mixins/eventTransmitter'

const {getOrderField, updateOrderField} = createHelpers({
  getterType: 'getOrderField',
  mutationType: 'updateOrderField',
})

const state = {
  salesOrderByConfirmation: {
    confirmationNumber: null,
    netsuite_internalId: null,
  },
  purchaseOrderByConfirmation: {
    confirmationNumber: null,
    netsuite_internalId: null,
  },
  recentSalesOrder: {
    id: null,
  },
  selectedSalesOrder: {
    id: null,
    custbody_retailtotal_pg: null,
    otherRefNum: null,
    custbody_platform_gold_id: null,
    custbody_salesrep_pg: null,
    tranDate: null,
    item: {
      items: [],
    },
  },
  selectedSalesOrderRetail: {
    id: null,
    salesOrderLineItem: [],
  },
  selectedSalesOrderRelated: {
    cp: [],
    if: [],
    u: [],
    r: [],
    aggregatedPaymentData: {},
  },
  salesOrders: [],
  pendingSalesOrders: [],
  recentPurchaseOrder: {
    id: null,
  },
  selectedPurchaseOrder: {
    id: null,
    custbody_platform_gold_id: null,
    custbody_salesrep_pg: null,
  },
  selectedPurchaseOrderRetail: {
    id: null,
    purchaseOrderLineItem: [],
  },
  selectedPurchaseOrderRelated: {
    po: [],
    ir: [],
    r: [],
  },
  selectedPurchaseOrderShipments: [],
  purchaseOrders: [],
  pendingPurchaseOrders: [],
  selectedLimitOrder: {},
  limitOrders: [],
      invoices: { rows: [], count: 0 },
  overdueInvoiceCount: 0,
  orderHtml: undefined,
  orderPdf: undefined,
  shippingQuotes: {},
}

function getOrderTax(salesOrder) {
  return Number(salesOrder.custbody_ucg_tax_total) || _.sumBy(salesOrder.item.items, 'custcol_ava_taxamount');
}

// getters
const getters = {
  getOrderField,
  getRecentSalesOrder: (state) => state.recentSalesOrder,
  getSalesOrders: (state) => state.salesOrders,
  getPendingSalesOrders: (state) => state.pendingSalesOrders,
  getSelectedSalesOrder: (state) => state.selectedSalesOrder,
  getSelectedSalesOrderRelated: (state) => state.selectedSalesOrderRelated,
  getSelectedSalesOrderRetail: (state) => state.selectedSalesOrderRetail,
  getSelectedSalesOrderTax: state => getOrderTax(state.selectedSalesOrder),
  getRecentPurchaseOrder: (state) => state.recentPurchaseOrder,
  getSalesOrderByConfirmation: (state) => state.salesOrderByConfirmation,
  getPurchaseOrders: (state) => state.purchaseOrders,
  getPendingPurchaseOrders: (state) => state.pendingPurchaseOrders,
  getSelectedPurchaseOrder: (state) => state.selectedPurchaseOrder,
  getSelectedPurchaseOrderRetail: (state) => state.selectedPurchaseOrderRetail,
  getSelectedPurchaseOrderRelated: (state) =>
    state.selectedPurchaseOrderRelated,
  getSelectedPurchaseOrderShipments: (state) => state.selectedPurchaseOrderShipments,
  getPurchaseOrderByConfirmation: (state) => state.purchaseOrderByConfirmation,
  getSelectedLimitOrder: (state) => state.selectedLimitOrder,
  getLimitOrders: (state) => state.limitOrders,
  getInvoices: (state) => state.invoices,
  getOverdueInvoiceCount: (state) => state.overdueInvoiceCount,
  getOrderHtml: (state) => state.orderHtml,
  getOrderPdf: (state) => state.orderPdf,
  getShippingQuotes: (state) => state.shippingQuotes,
}

/**
 *
 * @param cartItem
 * @param spotPrices
 * @param company
 * @param soPo
 * @returns {{unitPrice: number, quantity: (null|number|number|*), productId}}
 */
const formatLineItems = (cartItem, spotPrices, company, soPo = `so`) => {
  const markups = [
    {
      field: `markup`,
      type: `markupType`,
      percent: `retailPercentMarkup`,
      flat: `retailFlatMarkup`,
    },
    {
      field: `markupRep`,
      type: `markupTypeRep`,
      percent: `retailPercentMarkupRep`,
      flat: `retailFlatMarkupRep`,
    },
  ]
  const markupFields = {}
  for (const markup of markups) {
    if (cartItem[markup.percent] || cartItem[markup.flat]) {
      markupFields[markup.type] = cartItem[markup.percent]
        ? MarkupTypes.PERCENT
        : MarkupTypes.FLAT
      markupFields[markup.field] =
        markupFields[markup.type] === MarkupTypes.PERCENT
          ? cartItem[markup.percent]
          : cartItem[markup.flat]
    } else {
      markupFields[markup.type] = undefined
      markupFields[markup.field] = undefined
    }
  }
  const {bid, ask} = productMixin.methods.computePrices(
    cartItem.item,
    spotPrices,
    company
  )
  return Object.assign(
    {
      quantity: cartItem.quantity,
      unitPrice: soPo === `so` ? ask : bid,
      productId: cartItem.item.id,
    },
    markupFields
  )
}

// actions
const actions = {
  async fetchInvoices({ commit, getters }, { options, query }) {
    const companyId = getters.getSelectedCompany.id
    if (!companyId) return
    const paginationParams = optionsToPaginationParams(options)
    const { data } = await request({
      url: `/companies/${companyId}/invoices`,
      method: 'get',
      params: { query, ...paginationParams },
    })
    commit('SET_INVOICES', data)
    return data
  },
  async fetchOverdueInvoiceCount({commit, getters}) {
    const companyId = getters.getSelectedCompany.id
    if (!companyId) {
      commit('SET_OVERDUE_INVOICE_COUNT', 0)
      return 0
    }
    const {count} = await request({
      url: `/companies/${companyId}/invoices/overdue-count`,
      method: 'get',
    })
    const overdueCount = Number(count) || 0
    commit('SET_OVERDUE_INVOICE_COUNT', overdueCount)
    return overdueCount
  },
  async fetchInvoiceDetail({ getters }, { invoiceId }) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/companies/${companyId}/invoices/${invoiceId}`,
      method: 'get',
    }).then((data) => data)
  },
  async updateCustomerReference({ getters }, { invoiceId, customer_reference_number }) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/companies/${companyId}/invoices/${invoiceId}/customer-reference`,
      method: 'put',
      data: { customer_reference_number },
    }).then((data) => data)
  },
  async checkPaymentEligibility({ getters }) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/companies/${companyId}/invoices/payment-eligibility`,
      method: 'get',
    }).then((data) => data)
  },
  async submitInvoicePayment({ getters }, { invoiceIds }) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/companies/${companyId}/invoices/pay`,
      method: 'post',
      data: { invoiceIds },
    }).then((data) => (data && data.result ? data.result : data))
  },
  async submitOrderPayment({ getters }, { orderIds }) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/companies/${companyId}/orders/pay`,
      method: 'post',
      data: { orderIds },
    }).then((data) => (data && data.result ? data.result : data))
  },
  async placeShippingHold({getters, commit}, {id, data}) {
    return request({
      url: `/shipping-holds/place/${getters.getSelectedCompany.id}/${id}`,
      method: 'put',
      data,
    })
  },
  async releaseShippingHold({getters, commit}, {id, data}) {
    return request({
      url: `/shipping-holds/release/${getters.getSelectedCompany.id}/${id}`,
      method: 'put',
      data
    })
  },
  async fillLimitOrder({getters, commit}, {id}) {
    return request({
      url: `/limit-orders/fill`,
      method: 'post',
      data: {limitOrderId: id},
    })
  },
  async updateSalesOrder({getters, commit}, {id, data}) {
    return request({
      url: `/sales-orders/${getters.getSelectedCompany.id}/${id}`,
      method: 'put',
      data,
    })
  },
  async updatePurchaseOrder({getters, commit}, {id, data}) {
    return request({
      url: `/purchase-orders/${getters.getSelectedCompany.id}/${id}`,
      method: 'put',
      data,
    })
  },
  async downloadOrdersCsv(
    {getters, dispatch},
    {filename, type, dateRange}
  ) {
    const uriPart = type === `so` ? `sales-orders` : `purchase-orders`
    const params = dateRange
      ? `?dateRange=${encodeURIComponent(dateRange)}`
      : ``
    const url = `/${uriPart}/${getters.getSelectedCompany.id}/csv${params}`
    return dispatch(`_downloadFile`, {filename, url}, `application/csv`)
  },
  async downloadInvoicesCsv(
    {getters, dispatch},
    {filename, dateRange}
  ) {
    const params = dateRange
      ? `?dateRange=${encodeURIComponent(dateRange)}`
      : ``
    const url = `/companies/${getters.getSelectedCompany.id}/invoices/csv${params}`
    return dispatch(`_downloadFile`, {filename, url}, `application/csv`)
  },
  async emailInvoices(
    {getters, dispatch},
    {dateRange}
  ) {
    const params = dateRange
      ? `?dateRange=${encodeURIComponent(dateRange)}`
      : ``
    const url = `/companies/${getters.getSelectedCompany.id}/invoices/email${params}`
    return request({
      url,
      method: 'post',
      data: { dateRange }
    })
  },
  async downloadSalesOrder({getters, dispatch}, {id, filename}) {
    const url = `/sales-orders/${getters.getSelectedCompany.id}/${id}/pdf/branding/ucg`
    return dispatch(`_downloadFile`, {filename, url})
  },
  async downloadRetailSalesOrder({getters, dispatch}, {id, filename}) {
    const url = `/sales-orders/${getters.getSelectedCompany.id}/${id}/retail-pdf`
    return dispatch(`_downloadFile`, {filename, url})
  },
  async downloadPurchaseOrder({getters, dispatch}, {id, filename}) {
    const url = `/purchase-orders/${getters.getSelectedCompany.id}/${id}/pdf/branding/ucg`
    return dispatch(`_downloadFile`, {filename, url})
  },
  async downloadRetailPurchaseOrder({getters, dispatch}, {id, filename}) {
    const url = `/purchase-orders/${getters.getSelectedCompany.id}/${id}/retail-pdf`
    return dispatch(`_downloadFile`, {filename, url})
  },
  // eslint-disable-next-line no-empty-pattern
  async _downloadFile({}, {filename, url, type = 'application/pdf'}) {
    return request({
      url,
      method: 'get',
      responseType: 'arraybuffer',
    }).then((response) => {
      let blob = new Blob([response], {type})
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
    })
  },
  async htmlSalesOrder({getters, commit}, {id}) {
    return request({
      url: `/sales-orders/${getters.getSelectedCompany.id}/${id}/html/branding/ucg`,
      method: 'get',
    })
  },
  async htmlRetailSalesOrder({getters, commit}, {id}) {
    return request({
      url: `/sales-orders/${getters.getSelectedCompany.id}/${id}/retail-html`,
      method: 'get',
    })
  },
  async htmlRetailPurchaseOrder({getters, commit}, {id}) {
    return request({
      url: `/purchase-orders/${getters.getSelectedCompany.id}/${id}/retail-html`,
      method: 'get',
    })
  },
  async htmlPurchaseOrder({getters, commit}, {id}) {
    return request({
      url: `/purchase-orders/${getters.getSelectedCompany.id}/${id}/html/branding/ucg`,
      method: 'get',
    })
  },
  async emailSalesOrder({getters, commit}, {id, data}) {
    const display = 0 //Do not render email in response
    return request({
      url: `/sales-orders/${getters.getSelectedCompany.id}/${id}/email/${display}/branding/ucg`,
      method: 'post',
      data,
    })
  },
  async emailLimitOrder({getters, commit}, {id, data}) {
    return request({
      url: `/limit-orders/${getters.getSelectedCompany.id}/${id}/email`,
      method: 'post',
      data,
    })
  },
  async emailRetailSalesOrder({getters, commit}, {id, data}) {
    const display = 0 //Do not render email in response
    return request({
      url: `/sales-orders/${getters.getSelectedCompany.id}/${id}/retail-email/${display}`,
      method: 'post',
      data,
    })
  },
  async emailRetailPurchaseOrder({getters, commit}, {id, data}) {
    const display = 0 //Do not render email in response
    return request({
      url: `/purchase-orders/${getters.getSelectedCompany.id}/${id}/retail-email/${display}`,
      method: 'post',
      data,
    })
  },
  async emailPurchaseOrder({getters, commit}, {id, data}) {
    const display = 0 //Do not render email in response
    return request({
      url: `/purchase-orders/${getters.getSelectedCompany.id}/${id}/email/${display}/branding/ucg`,
      method: 'post',
      data,
    })
  },
  async downloadInvoice({getters, dispatch}, {id, filename}) {
    const url = `/companies/${getters.getSelectedCompany.id}/invoices/${id}/pdf/branding/ucg`
    return dispatch(`_downloadFile`, {filename, url})
  },
  async htmlInvoice({getters, commit}, {id}) {
    return request({
      url: `/companies/${getters.getSelectedCompany.id}/invoices/${id}/html/branding/ucg`,
      method: 'get',
    })
  },
  async emailInvoice({getters, commit}, {id, data}) {
    const display = 0 //Do not render email in response
    return request({
      url: `/companies/${getters.getSelectedCompany.id}/invoices/${id}/email/${display}/branding/ucg`,
      method: 'post',
      data,
    })
  },
  async fetchSalesOrder({getters, commit, dispatch}, {id}) {
    return request({
      url: `/sales-orders/${getters.getSelectedCompany.id}/${id}`,
      method: 'get',
    })
      .then((data) => {
        commit('SET_SELECTED_SALES_ORDER', data)
      })
      .catch(async (err) => {
        if (err.response.status === 400 && guardsMixin.methods.isAdmin()) {
          const company = await dispatch(`getSalesOrderCompany`, {id})
          if (company.id !== getters.getSelectedCompany.id) {
            return dispatch(`fetchAndSelectCompany`, company.id).then(() => {
              window._VMA.$emit('SHOW_SNACKBAR', {
                text: `${company.name} is now your active company`,
                color: 'success',
              })
              return dispatch(`fetchSalesOrder`, {id})
            })
          }
        }
        throw err
      })
  },
  async fetchSalesOrderRetail({getters, commit, dispatch}, {id}) {
    return request({
      url: `/sales-orders/${getters.getSelectedCompany.id}/${id}/retail`,
      method: 'get',
    })
      .then((data) => {
        commit('SET_SELECTED_SALES_ORDER_RETAIL', data)
      })
      .catch(async (err) => {
        if (err.response.status === 400 && guardsMixin.methods.isAdmin()) {
          const company = await dispatch(`getSalesOrderCompany`, {id})
          if (company.id !== getters.getSelectedCompany.id) {
            return dispatch(`fetchAndSelectCompany`, company.id).then(() => {
              window._VMA.$emit('SHOW_SNACKBAR', {
                text: `${company.name} is now your active company`,
                color: 'success',
              })
              return dispatch(`fetchSalesOrderRetail`, {id})
            })
          }
        }
        throw err
      })
  },
  async getSalesOrderCompany({getters, commit}, {id}) {
    return request({
      url: `/sales-orders/company/${id}`,
      method: 'get',
    })
  },
  async fetchSalesOrderRelated({getters, commit}, {id}) {
    return request({
      url: `/sales-orders/${getters.getSelectedCompany.id}/${id}/related`,
      method: 'get',
    }).then((data) => {
      commit('SET_SELECTED_SALES_ORDER_RELATED', data)
    })
  },
  async fetchSalesOrders({getters, commit}, {options, query}) {
    const paginationParams = optionsToPaginationParams(options)
    const lastId = _.get(getters, 'getRecentSalesOrder.id', undefined)
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/sales-orders/${companyId}`,
      params: {query, ...paginationParams, lastId},
      method: 'get',
    }).then((data) => {
      commit('SET_SALES_ORDERS', data)
    })
  },
  async fetchPendingSalesOrders({getters, commit}) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/sales-orders/${companyId}/pending`,
      method: 'get',
    }).then((data) => {
      commit('SET_PENDING_SALES_ORDERS', data)
    })
  },
  async fetchPendingPurchaseOrders({getters, commit}) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/purchase-orders/${companyId}/pending`,
      method: 'get',
    }).then((data) => {
      commit('SET_PENDING_PURCHASE_ORDERS', data)
    })
  },
  async fetchPurchaseOrderRelated({getters, commit}, {id}) {
    return request({
      url: `/purchase-orders/${getters.getSelectedCompany.id}/${id}/related`,
      method: 'get',
    }).then((data) => {
      commit('SET_SELECTED_PURCHASE_ORDER_RELATED', data)
    })
  },
  async fetchPurchaseOrder({getters, commit, dispatch}, {id}) {
    return request({
      url: `/purchase-orders/${getters.getSelectedCompany.id}/${id}`,
      method: 'get',
    })
      .then((data) => {
        commit('SET_SELECTED_PURCHASE_ORDER', data)
      })
      .catch(async (err) => {
        if (err.response.status === 400 && guardsMixin.methods.isAdmin()) {
          const company = await dispatch(`getPurchaseOrderCompany`, {id})
          if (company.id !== getters.getSelectedCompany.id) {
            return dispatch(`fetchAndSelectCompany`, company.id).then(() => {
              window._VMA.$emit('SHOW_SNACKBAR', {
                text: `${company.name} is now your active company`,
                color: 'success',
              })
              return dispatch(`fetchPurchaseOrder`, {id})
            })
          }
        }
        throw err
      })
  },
  async fetchPurchaseOrderRetail({getters, commit, dispatch}, {id}) {
    return request({
      url: `/purchase-orders/${getters.getSelectedCompany.id}/${id}/retail`,
      method: 'get',
    })
      .then((data) => {
        commit('SET_SELECTED_PURCHASE_ORDER_RETAIL', data)
      })
      .catch(async (err) => {
        if (err.response.status === 400 && guardsMixin.methods.isAdmin()) {
          const company = await dispatch(`getPurchaseOrderCompany`, {id})
          if (company.id !== getters.getSelectedCompany.id) {
            return dispatch(`fetchAndSelectCompany`, company.id).then(() => {
              window._VMA.$emit('SHOW_SNACKBAR', {
                text: `${company.name} is now your active company`,
                color: 'success',
              })
              return dispatch(`fetchPurchaseOrderRetail`, {id})
            })
          }
        }
        throw err
      })
  },
  async getPurchaseOrderCompany({getters, commit}, {id}) {
    return request({
      url: `/purchase-orders/company/${id}`,
      method: 'get',
    })
  },
  async fetchPurchaseOrders({getters, commit}, {options, query}) {
    const paginationParams = optionsToPaginationParams(options)
    const lastId = _.get(getters, 'getRecentPurchaseOrder.id', undefined)
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/purchase-orders/${companyId}`,
      params: {query, ...paginationParams, lastId},
      method: 'get',
    }).then((data) => {
      commit('SET_PURCHASE_ORDERS', data)
    })
  },
  async submitSalesOrder(
    {commit, getters, rootState, rootGetters, dispatch},
    {spotPrices, confirmationNumber}
  ) {
    const cartState = getters.getCartState
    // If EquityTrust or Depository order, build out depository address object with customer account information
    const selectedAddress = cartState.buy.selectedDepoAddress
    const shippingAddress =
      cartState.buy.fulfillmentType === FulfillmentTypes.EQUITY_TRUST ||
      (parseInt(cartState.buy.selectedShippingInstructions.netsuite_internalId) === orderMixin.data().DEPOSITORY_INSTRUCTIONS_NS_ID
        && cartState.buy.selectedDepoAddress.id !== 'OTHER')
        ? {
          shipAttention: cartState.buy.fulfillmentType === FulfillmentTypes.EQUITY_TRUST
            ? `${cartState.buy.equityTrustLinkedAccount.ownerContactName} #${cartState.buy.equityTrustAccountNumber}`
            : `${cartState.buy.standardDepoAttnName || "TBD"}${orderMixin.data().DEPO_ATTENTION_DELIM}${cartState.buy.standardDepoAccountNumber || "TBD"}`,
          //TODO: Make sure we actually want this label for ET/Depo orders.
          shipAddressee: cartState.buy.fulfillmentType === FulfillmentTypes.EQUITY_TRUST
            ? selectedAddress.label
            : selectedAddress.addressee,
          shipAddr1: selectedAddress.street1,
          shipAddr2: selectedAddress.street2,
          shipCity: selectedAddress.city,
          shipState: selectedAddress.state,
          shipZip: selectedAddress.zip,
          shipCountry: selectedAddress.country
        }
        : undefined

    let retailTotal = cartState.buy.retailTotal
    let handlingFeeMarkupType, handlingFeeMarkup
    if (retailTotal) {
      handlingFeeMarkupType = cartState.buy.retailHandlingPercentMarkup
        ? MarkupTypes.PERCENT
        : MarkupTypes.FLAT
      handlingFeeMarkup =
        handlingFeeMarkupType === MarkupTypes.FLAT
          ? cartState.buy.retailHandlingFlatMarkup
          : cartState.buy.retailHandlingPercentMarkup
    } else {
      handlingFeeMarkupType = undefined
      handlingFeeMarkup = undefined
      retailTotal = undefined
    }
    const data = {
      confirmationNumber,
      salesRep: cartState.buy.salesRep,
      orderDate: new Date().toISOString().slice(0, 10),
      customerPo: cartState.buy.customerReference,
      handlingFee: getters.getBuyHandlingFee,
      snapshotSpotPriceGold: spotPrices.Gold.ask,
      snapshotSpotPriceSilver: spotPrices.Silver.ask,
      snapshotSpotPricePlatinum: spotPrices.Platinum.ask,
      snapshotSpotPricePalladium: spotPrices.Palladium.ask,
      paymentMethodId: cartState.buy.selectedPaymentMethod.id,
      companyId: getters.getSelectedCompany.id,
      salesOrderLineItems: cartState.buy.items.map((cartItem) => {
        return formatLineItems(
          cartItem,
          spotPrices,
          getters.getSelectedCompany,
          `so`
        )
      }),
      billingCompanyLocationId: cartState.buy.selectedBillingAddress.id,
      //Only send through LocationId if we have one AND we're not using the ShippingAddress
      //overide for depos.
      shippingCompanyLocationId: cartState.buy.selectedShippingAddress.id
      && shippingAddress === undefined
        ? cartState.buy.selectedShippingAddress.id
        : undefined,
      shippingAddress,
      shippingInstructionId: cartState.buy.selectedShippingInstructions.id,
      orderNotes: cartState.buy.orderNotes,
      equityTrustAccountNumber: cartState.buy.fulfillmentType === FulfillmentTypes.EQUITY_TRUST
        ? cartState.buy.equityTrustAccountNumber
        : undefined,
      equityTrustDepositoryId: cartState.buy.fulfillmentType === FulfillmentTypes.EQUITY_TRUST
        ? cartState.buy.selectedDepoAddress.asset_provider_id
        : undefined,
      equityTrustDepositoryEnum: cartState.buy.fulfillmentType === FulfillmentTypes.EQUITY_TRUST
        ? cartState.buy.selectedDepoAddress.et_depository_enum
        : undefined,
      achDebitDate: (ACH_PAYMENT_IDS.includes(cartState.buy.selectedPaymentMethod.netsuite_internalId))
        ? orderMixin.methods.minAchDebitDate([cartState.buy.achDebitDate, orderMixin.methods.nextMarketClose()])
        : undefined,
      markups: JSON.stringify(cartState.buy.retailGlobalMarkups),
      handlingFeeMarkupType,
      handlingFeeMarkup,
      retailTotal,
      // Shipping hold data - backend will apply after NetSuite sync completes
      shippingHoldDate: cartState.buy.shippingHoldEnabled ? cartState.buy.shippingHoldDate : undefined,
      // Only send a previous instruction id when shipping hold is enabled AND the
      // user has a valid (non-null) shipping instruction selected.  Sending undefined
      // (omitted from payload) lets the server fall back to HOLD_FOR_INSTRUCTIONS,
      // which is the correct behaviour when no instruction has been chosen yet.
      shippingHoldPreviousInstructionId: cartState.buy.shippingHoldEnabled && _.get(cartState, 'buy.selectedShippingInstructions.id')
        ? cartState.buy.selectedShippingInstructions.id
        : undefined,
    }
    return request({
      url: '/sales-orders/create',
      data,
      method: 'post',
    }).then(async (response) => {
      const {confirmationNumber, netsuite_internalId} = response;
      commit('SET_SO_BY_CONFIRMATION', {confirmationNumber, netsuite_internalId});
      await sendEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], {
        eventName: ANALYTICS_EVENTS.PURCHASE,
        bid_ask: 'ask',
        transaction_id: data.confirmationNumber,
        value: data.handlingFee + data.salesOrderLineItems.reduce((acc, item) => acc + item.unitPrice * item.quantity, 0),
        shipping: data.handlingFee,
        currency: 'USD',
        items: cartState.buy.items.map((item) => {
          return {
            item_id: item.item?.netsuite_internalId,
            item_name: item.item?.name,
            quantity: item.quantity,
            price: data.salesOrderLineItems.find(l => l.productId === item.item.id).unitPrice
          }
        })
      }, { getters: rootGetters, dispatch })
    })
  },
  async submitPurchaseOrder(
    {commit, getters, rootState, rootGetters, dispatch},
    {spotPrices, confirmationNumber}
  ) {
    const cartState = getters.getCartState
    const retailTotal = cartState.sell.retailTotal
    const applyMpf = !!orderMixin.methods.getMerchandiseProcessingFee(`sell`, getters.getSelectedCompany, retailTotal)
    const data = {
      confirmationNumber,
      retailTotal,
      salesRep: cartState.sell.salesRep,
      orderDate: new Date().toISOString().slice(0, 10),
      customerSo: cartState.sell.customerReference,
      handlingFee: getters.getSellHandlingFee,
      snapshotSpotPriceGold: spotPrices.Gold.bid,
      snapshotSpotPriceSilver: spotPrices.Silver.bid,
      snapshotSpotPricePlatinum: spotPrices.Platinum.bid,
      snapshotSpotPricePalladium: spotPrices.Palladium.bid,
      processingDelayMessage: cartState.sell.processingDelayMessage,
      paymentMethodId: cartState.sell.selectedPaymentMethod.id,
      companyId: getters.getSelectedCompany.id,
      purchaseOrderLineItems: cartState.sell.items.map((cartItem) => {
        return formatLineItems(
          cartItem,
          spotPrices,
          getters.getSelectedCompany,
          `po`
        )
      }),
      billingCompanyLocationId: cartState.sell.selectedBillingAddress.id,
      warehouseLocationId: cartState.sell.selectedShippingAddress.id,
      orderNotes: cartState.sell.orderNotes,
      applyMpf,
      preSettlementPreview: cartState.sell.preSettlementPreview,
    }
    return request({
      url: '/purchase-orders/create',
      data,
      method: 'post',
    }).then(async (response) => {
      const {confirmationNumber, netsuite_internalId} = response;
      commit('SET_PO_BY_CONFIRMATION', {confirmationNumber, netsuite_internalId});
      await sendEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], {
        eventName: ANALYTICS_EVENTS.PURCHASE,
        bid_ask: 'bid',
        transaction_id: data.confirmationNumber,
        value: data.purchaseOrderLineItems.reduce((acc, item) => acc + item.unitPrice * item.quantity, 0),
        currency: 'USD',
        items: cartState.sell.items.map((item) => {
          return {
            item_id: item.item?.netsuite_internalId,
            item_name: item.item?.name,
            quantity: item.quantity,
            price: data.purchaseOrderLineItems.find(l => l.productId === item.item.id).unitPrice
          }
        })
      }, { getters: rootGetters, dispatch })
    })
  },
  async submitLimitOrder({commit, getters, rootState, rootGetters, dispatch}, props) {
    const cartState = getters.getCartState
    const {buySell} = props
    const data = {
      ...props,
      orderDate: new Date().toISOString().slice(0, 10),
      companyId: getters.getSelectedCompany.id,
      paymentMethodId: cartState[buySell].selectedPaymentMethod.id,
      billingCompanyLocationId: cartState[buySell].selectedBillingAddress.id,
      shippingCompanyLocationId:
        cartState[buySell].selectedShippingAddress.id || undefined,
      shippingInstructionId: cartState[buySell].selectedShippingInstructions.id,
    }
    return request({
      url: '/limit-orders/create',
      data,
      method: 'post',
    }).then(async (data) => {
      commit('SET_SELECTED_LIMIT_ORDER', data)
      await sendEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], {
        eventName: ANALYTICS_EVENTS.LIMIT_CREATE,
        item_id: data.product?.netsuite_internalId,
        quantity: data.qty,
        limit_price: data.limitPrice,
        buy_or_sell: buySell
      }, { getters: rootGetters, dispatch })
      return data
    })
  },
  async cancelLimitOrder({commit, getters, rootState, rootGetters, dispatch}, props) {
    const {id} = props
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/limit-orders/${companyId}/${id}/cancel`,
      method: 'post',
    }).then(async (resp)=>{
      await sendEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], { eventName: ANALYTICS_EVENTS.LIMIT_CANCEL, limit_order_id: id }, { getters: rootGetters, dispatch })
      return resp
    })
  },
  async fetchLimitOrders({commit, getters}, {options, query}) {
    const paginationParams = optionsToPaginationParams(options)
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/limit-orders/${companyId}`,
      method: 'get',
      params: {query, ...paginationParams},
    }).then((data) => {
      commit('SET_LIMIT_ORDERS', data)
    })
  },
  async fetchLimitOrdersAdmin({commit, getters}, {options, query}) {
    const paginationParams = optionsToPaginationParams(options)
    return request({
      url: `/limit-orders/admin`,
      method: 'get',
      params: {query, ...paginationParams},
    }).then((data) => {
      commit('SET_LIMIT_ORDERS', data)
    })
  },
  async fetchLimitOrder({commit, getters}, limitOrderId) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/limit-orders/${companyId}/${limitOrderId}`,
      method: 'get',
    }).then((data) => {
      commit('SET_SELECTED_LIMIT_ORDER', data)
    })
  },
  async fetchSalesOrderByConfirmation({getters, commit}, confirmationNumber) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/sales-orders/${companyId}/pending/${confirmationNumber}`,
      method: 'get',
    })
  },
  async fetchPurchaseOrderByConfirmation(
    {getters, commit},
    confirmationNumber
  ) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/purchase-orders/${companyId}/pending/${confirmationNumber}`,
      method: 'get',
    })
  },
  async fetchPurchaseOrderShipments({getters, commit}, {purchaseOrderId}) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/purchase-orders/${companyId}/${purchaseOrderId}/shipments`,
      method: 'get',
    }).then((data) => {
      commit('SET_SELECTED_PURCHASE_ORDER_SHIPMENTS', data)
    })
  },
  async fetchPurchaseOrderShipmentQuote(
    {getters, commit},
    {purchaseOrderId, data}
  ){
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/purchase-orders/${companyId}/${purchaseOrderId}/rate`,
      data,
      method: 'post',
    })
  },
  async listPurchaseOrderShipmentQuotes(
    {getters, commit},
    {purchaseOrderId, data}
  ){
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/purchase-orders/${companyId}/${purchaseOrderId}/shipment/rates`,
      data,
      method: 'post',
    })
  },
  async createPurchaseOrderShipment(
    {getters, commit},
    {purchaseOrderId, data, operationMeta = {}}
  ) {
    const companyId = getters.getSelectedCompany.id
    const operationId = `create-${purchaseOrderId}-${Date.now()}`
    commit('ADD_SHIPSECURE_PENDING_OPERATION', {
      id: operationId, type: 'create', entityType: 'purchaseOrder',
      entityId: purchaseOrderId, ...operationMeta,
    })
    try {
      return await request({
        url: `/purchase-orders/${companyId}/${purchaseOrderId}/shipment`,
        data,
        method: 'post',
      })
    } finally {
      commit('REMOVE_SHIPSECURE_PENDING_OPERATION', operationId)
    }
  },
  async cancelPurchaseOrderShipment(
    {getters, commit},
    {purchaseOrderId, shipmentId, operationMeta = {}}
  ) {
    const companyId = getters.getSelectedCompany.id
    const operationId = `delete-${shipmentId}-${Date.now()}`
    commit('ADD_SHIPSECURE_PENDING_OPERATION', {
      id: operationId, type: 'delete', entityType: 'purchaseOrder',
      entityId: purchaseOrderId, shipmentId, ...operationMeta,
    })
    try {
      return await request({
        url: `/purchase-orders/${companyId}/${purchaseOrderId}/shipment/${shipmentId}`,
        method: 'delete',
      })
    } finally {
      commit('REMOVE_SHIPSECURE_PENDING_OPERATION', operationId)
    }
  },
  async validatePurchaseOrderShipment(
    {getters, commit},
    {purchaseOrderId, data}
  ) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/purchase-orders/${companyId}/${purchaseOrderId}/shipment/validate`,
      data,
      method: 'post',
    })
  },
  async approvePurchaseOrderAdjustments({getters, commit}, {purchaseOrderId, adjustmentIds}) {
    const companyId = getters.getSelectedCompany.id
    return request({
      url: `/purchase-orders/${companyId}/${purchaseOrderId}/approve-adjustments`,
      method: 'post',
      data: {adjustmentIds},
    })
  },
}

// mutations
const mutations = {
  updateOrderField,
  SET_RECENT_SALES_ORDER(state, data) {
    state.recentSalesOrder = data
  },
  SET_SALES_ORDERS(state, data) {
    state.salesOrders = data
  },
  SET_PENDING_SALES_ORDERS(state, data) {
    state.pendingSalesOrders = data
  },
  SET_PENDING_PURCHASE_ORDERS(state, data) {
    state.pendingPurchaseOrders = data
  },
  SET_SELECTED_SALES_ORDER(state, data) {
    state.selectedSalesOrder = data
  },
  SET_SELECTED_SALES_ORDER_RELATED(state, data) {
    state.selectedSalesOrderRelated = data
  },
  SET_SELECTED_SALES_ORDER_RETAIL(state, data) {
    state.selectedSalesOrderRetail = data
  },
  SET_SELECTED_PURCHASE_ORDER_RELATED(state, data) {
    state.selectedPurchaseOrderRelated = data
  },
  SET_SELECTED_PURCHASE_ORDER_RETAIL(state, data) {
    state.selectedPurchaseOrderRetail = data
  },
  SET_RECENT_PURCHASE_ORDER(state, data) {
    state.recentPurchaseOrder = data
  },
  SET_PURCHASE_ORDERS(state, data) {
    state.purchaseOrders = data
  },
  SET_SELECTED_PURCHASE_ORDER(state, data) {
    state.selectedPurchaseOrder = data
  },
  SET_SO_BY_CONFIRMATION(state, data) {
    state.salesOrderByConfirmation = data
  },
  SET_PO_BY_CONFIRMATION(state, data) {
    state.purchaseOrderByConfirmation = data
  },
  SET_SELECTED_LIMIT_ORDER(state, data) {
    state.selectedLimitOrder = data
  },
  SET_LIMIT_ORDERS(state, data) {
    state.limitOrders = data
  },
  SET_SELECTED_PURCHASE_ORDER_SHIPMENTS(state, data) {
    state.selectedPurchaseOrderShipments = data
  },
  SET_INVOICES(state, data) {
          state.invoices = data;
  },
  SET_OVERDUE_INVOICE_COUNT(state, count) {
    state.overdueInvoiceCount = Number(count) || 0
  },
  setOrderHtml(state, orderHtml) {
    state.orderHtml = orderHtml
  },
  setOrderPdf(state, orderPdf) {
    state.orderPdf = orderPdf
  },
}

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
}